<script setup lang="ts">
import { computed, useEventListener, useTemplateRef, useElementSize, whenever, definePageMeta, usePreviewBridge, previewGame } from '#imports'
// Namespace import, not `{ z }`: Vite drops the re-exported `z` binding, so the
// named form resolves to undefined under the Nuxt test environment.
import * as z from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

/**
 * Mirrored by TileGen's preview payload constant — the keys below are the contract
 * between the two, so update both together. Images are data URLs: TileGen drafts sit
 * behind Cloudflare Access, so the site could not fetch them by URL.
 */
const DataSchema = z.looseObject({
    name: z.string().default(''),
    logo: z.string().default(''),
    background: z.string().default(''),
    /** Bottom-fade colour; defaults to the tile's own fallback when absent. */
    color: z.string().optional(),
})

const { data, send } = usePreviewBridge(DataSchema)

const game = computed(() => previewGame(data.value?.data?.name ?? '', data.value?.data?.color))

// The slot is the embedder's width; the tile keeps the rotator's 16/11 ratio and
// 344px cap, so the frame reports its height and lets the width be.
const slot = useTemplateRef<HTMLDivElement>('slot')
const { height } = useElementSize(slot)

whenever(height, h => send({ type: 'resize', payload: { height: `${h}px`, width: '100%' } }))

// The tile opens a game on click; swallow it before it reaches the handler.
if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, { capture: true })
}
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <div
                v-else
                ref="slot"
                class="preview-hero-rotator"
                data-test="preview-hero-rotator"
            >
                <NimbusHeroRotatorTile
                    :game="game"
                    :background-src="data.data.background"
                    :logo-src="data.data.logo"
                    :color="data.data.color"
                    eager
                />
            </div>
        </template>
    </UIApp>
</template>

<style scoped lang="scss">
// Geometry copied from the rotator's `&__tile`: 16/11, mobile 225px tall, capped at 344px on tablet and up.
.preview-hero-rotator {
    position: relative;
    width: 100%;
    height: 225px;
    border-radius: var(--border-radius-lg, $border-radius-lg);
    overflow: hidden;

    @include tablet(md) {
        height: auto;
        max-height: 344px;
        aspect-ratio: 16 / 11;
    }
}
</style>
